/**
 * Internationalization actions
 */
import { getInitialLocale, persistLocale } from '../i18n/locale-helper'
import { createBoundActions } from '../common/bound-actions'
import { DEFAULT_CONTEXT_PATH } from '../common/constants'
import reportError from '../common/report-error'

const TRANSLATIONS_RESOURCE_NAME = 'translations'
const TRANSLATIONS_RESOURCE_KEY = 'stringCode'
const TRANSLATION_RESOURCE_PATH = '/' + TRANSLATIONS_RESOURCE_NAME

const UI_GENERIC_CONTEXT_CODE = 'UI_GENERIC'

export const ADD_MODULE_CONTEXT_CODE_SUCCESS = 'ADD_MODULE_CONTEXT_CODE_SUCCESS'
export const UPDATE_TRANSLATIONS_SUCCESS = 'UPDATE_TRANSLATIONS_SUCCESS'

function fetchTranslationsAction(translationActions, contextCode, locale = 'en-GB') {
    return translationActions.fetch({
        filter: { contextCode, locale, project: 'basic' },
        forceUpdate: true
    })
}

function addModuleContextCode(contextCode, locale) {
    return { type: ADD_MODULE_CONTEXT_CODE_SUCCESS, contextCode, locale }
}

function updateTranslationsSuccess(locale, translations) {
    return { type: UPDATE_TRANSLATIONS_SUCCESS, locale, translations }
}

function updateModuleTranslationsThunk(translationActions, contextCode) {
    return async (dispatch, getState) => {
        const { intl } = getState()
        const { locale, moduleContextCodes = {} } = intl
        const haveModuleContextCode = moduleContextCodes[contextCode]
        const haveModuleContextTranslations = moduleContextCodes?.[contextCode]?.[locale]
        if (!haveModuleContextCode || !haveModuleContextTranslations) {
            const result = await dispatch(fetchTranslationsAction(translationActions, contextCode, locale))
            const newTranslations = result?.data ?? []
            dispatch(updateTranslationsSuccess(locale, newTranslations))
            if (!haveModuleContextTranslations) {
                dispatch(addModuleContextCode(contextCode, locale))
            }
        }
    }
}

function changeLocaleThunk(translationActions, locale, globalContextCode) {
    return async function (dispatch, getState) {
        const deferred = []
        const { intl } = getState()
        const { moduleContextCodes = {}, translations } = intl
        const haveLocaleTranslations = translations?.[locale]
        // Only fetch global translations for "new" locales
        if (!haveLocaleTranslations) {
            // request global application translations if configured
            if (globalContextCode) {
                deferred.push(dispatch(fetchTranslationsAction(translationActions, globalContextCode, locale)))
            }
            // fetch the ui-generic context as all uis should fetch it so they common molecule/organismn libs
            deferred.push(dispatch(fetchTranslationsAction(translationActions, UI_GENERIC_CONTEXT_CODE, locale)))
        }
        const moduleContextCodesArray = Object.keys(moduleContextCodes)
        // request additional module-specific translations for registered module context codes
        if (moduleContextCodesArray.length > 0) {
            moduleContextCodesArray.forEach(moduleContextCode => {
                const haveModuleContextTranslations = moduleContextCodes?.[moduleContextCode]?.[locale]
                // Only fetch module specific translations for "new" contextCodes and "new" locales
                if (!haveLocaleTranslations || !haveModuleContextTranslations) {
                    deferred.push(dispatch(fetchTranslationsAction(translationActions, moduleContextCode, locale)))
                    if (!haveModuleContextTranslations) {
                        dispatch(addModuleContextCode(moduleContextCode, locale))
                    }
                }
            })
        }
        try {
            const result = await Promise.all(deferred)
            persistLocale(locale) // store locale for future use
            const newTranslations = []
            Array.isArray(result) &&
                result.forEach(resultItem => {
                    if (resultItem?.data) {
                        newTranslations.push(...resultItem.data) // collect global + module translations response data
                    }
                })
            dispatch(updateTranslationsSuccess(locale, newTranslations))
        } catch (error) {
            reportError(error)
        }
    }
}

export function createTranslationsActions({
    contextCode: globalContextCode,
    contextPath = DEFAULT_CONTEXT_PATH,
    resourcePath = TRANSLATION_RESOURCE_PATH
}) {
    const translationActions = createBoundActions(
        TRANSLATIONS_RESOURCE_NAME,
        contextPath,
        resourcePath,
        TRANSLATIONS_RESOURCE_KEY
    )

    return {
        fetchTranslations: (locale, contextCode, resetState) => {
            return fetchTranslationsAction(translationActions, contextCode, locale, resetState)
        },

        updateModuleTranslations: contextCode => {
            return updateModuleTranslationsThunk(translationActions, contextCode)
        },

        changeLocale: locale => {
            return changeLocaleThunk(translationActions, locale, globalContextCode)
        }
    }
}

export function createInitializeIntlThunk(changeLocaleAction, supportedLocales) {
    const initialLocale = getInitialLocale(supportedLocales)
    return function initializeIntlThunk(dispatch) {
        return dispatch(changeLocaleAction(initialLocale))
    }
}
